import { useQuery } from '@tanstack/react-query';
import { reportingService } from '../services/reportingService';
import { queryKeys } from '../config/queryKeys';
import {
  ExtendedReportRequest,
  DashboardSummary,
  JobsCreatedReport,
  LostJobsReport,
  ConversionFunnel,
  UserComparisonReport,
  LeadSourceReport,
  RevenueByMonthReport,
  RevenueByUserReport,
  CycleTimeReport,
  PresentationOutcomeReport,
  AppointmentCompletionReport,
  SpecBreakdownReport,
} from '../types/reporting';

interface UseReportParams extends ExtendedReportRequest {
  enabled?: boolean;
}

export function useDashboardSummary(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<DashboardSummary>({
    queryKey: queryKeys.reports.dashboardSummary(request),
    queryFn: () => reportingService.getDashboardSummary(request),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useJobsCreatedReport(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<JobsCreatedReport>({
    queryKey: queryKeys.reports.jobsCreated(request),
    queryFn: () => reportingService.getJobsCreatedReport(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLostJobsReport(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<LostJobsReport>({
    queryKey: queryKeys.reports.lostJobs(request),
    queryFn: () => reportingService.getLostJobsReport(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useConversionFunnel(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<ConversionFunnel>({
    queryKey: queryKeys.reports.conversionFunnel(request),
    queryFn: () => reportingService.getConversionFunnel(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUserComparison(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<UserComparisonReport>({
    queryKey: queryKeys.reports.userComparison(request),
    queryFn: () => reportingService.getUserComparison(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLeadSources(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<LeadSourceReport[]>({
    queryKey: queryKeys.reports.leadSources(request),
    queryFn: () => reportingService.getLeadSources(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRevenueByMonth(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<RevenueByMonthReport>({
    queryKey: queryKeys.reports.revenueByMonth(request),
    queryFn: () => reportingService.getRevenueByMonth(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRevenueByUser(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<RevenueByUserReport>({
    queryKey: queryKeys.reports.revenueByUser(request),
    queryFn: () => reportingService.getRevenueByUser(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCycleTime(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<CycleTimeReport>({
    queryKey: queryKeys.reports.cycleTime(request),
    queryFn: () => reportingService.getCycleTime(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePresentationOutcomes(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<PresentationOutcomeReport>({
    queryKey: queryKeys.reports.presentationOutcomes(request),
    queryFn: () => reportingService.getPresentationOutcomes(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAppointmentCompletion(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<AppointmentCompletionReport>({
    queryKey: queryKeys.reports.appointmentCompletion(request),
    queryFn: () => reportingService.getAppointmentCompletion(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSpecBreakdown(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  return useQuery<SpecBreakdownReport>({
    queryKey: queryKeys.reports.specBreakdown(request),
    queryFn: () => reportingService.getSpecBreakdown(request),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// Combined hook for fetching all dashboard data at once
export function useDashboardReports(params: UseReportParams = {}) {
  const { enabled = true, ...request } = params;

  const dashboardSummary = useDashboardSummary({ ...request, enabled });
  const jobsCreated = useJobsCreatedReport({ ...request, enabled });
  const conversionFunnel = useConversionFunnel({ ...request, enabled });
  const userComparison = useUserComparison({ ...request, enabled });
  const leadSources = useLeadSources({ ...request, enabled });

  return {
    dashboardSummary,
    jobsCreated,
    conversionFunnel,
    userComparison,
    leadSources,
    isLoading:
      dashboardSummary.isLoading ||
      jobsCreated.isLoading ||
      conversionFunnel.isLoading ||
      userComparison.isLoading ||
      leadSources.isLoading,
    isError:
      dashboardSummary.isError ||
      jobsCreated.isError ||
      conversionFunnel.isError ||
      userComparison.isError ||
      leadSources.isError,
  };
}
